import React, { useEffect, useRef, useState } from 'react';
import { Button, Checkbox, Modal, Popconfirm, Select, Space, Table, Tooltip } from 'antd';
import { DeleteOutlined, EditOutlined, EyeOutlined, PrinterOutlined } from '@ant-design/icons';
import HTTPService from '../../../utils/makerequest';
import { IPageData } from '../../../interfaces/page-data';
import { StateLoader } from '../../../redux/StateLoader';
import { IAuthentication, UserRole } from '../../../interfaces/authentication';
import { useTranslation } from 'react-i18next';
import { Toast } from 'primereact/toast';
import { Printer } from '../../../interfaces/IPrinterManagement';
import i18n from '../../../i18n';
import MUIDatagrid, { IMUIDataGridColumns } from '../../../AltisComponent/MUIDataGrid';
import { IDropdownItem } from '../../../AltisComponent/AltisDataGrid';

export interface IToken {
  deviceToken: string;
}

export interface PrinterDetails {
  printerDetails: {
    printerIsConnected: boolean;
    printMode: string;
    labelLengthInDots: number;
    numberOfFormatsInReceiveBuffer: number;
    labelsRemainingInBatch: number;
    isPartialFormatInProgress: boolean;
    isHeadCold: boolean;
    isHeadOpen: boolean;
    isHeadTooHot: boolean;
    isPaperOut: boolean;
    isRibbonOut: boolean;
    isReceiveBufferFull: boolean;
    isPaused: boolean;
    isReadyToPrint: boolean;
  };
}

const PrinterManagement: React.FunctionComponent<any> = (props) => {
  const { t } = useTranslation();
  const { onSetPage } = props;
  const loader = new StateLoader();
  const userInfo: IAuthentication = loader.loadAuthState();
  const [printerData, setPrinterData] = useState<Printer>();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedPrinter, setSelectedPrinter] = useState<Printer | null>(null);
  const dataGridRef = useRef<any>(null);
  const toast = useRef<any>(null);

  const pageData: IPageData = {
    title: t('Printer Management'),
    loaded: true,
  };

  useEffect(() => {
    onSetPage(pageData);
    dataGridRef.current.GetAllData()
  }, [i18n.language]);

  const printModeOptions: IDropdownItem[] = [
    { id: 'mode1', name: 'Mode 1' },
    { id: 'mode2', name: 'Mode 2' },
    { id: 'mode3', name: 'Mode 3' },
  ];

  const renderValue = (value: any) => {
    if (typeof value === 'boolean') {
      return <Checkbox checked={value} disabled />;
    }
    return value;
  };

  const DetailsColumn = [
    {
      title: t('key'),
      key: 'Key',
      dataIndex: 'Key',
    },
    {
      title: t('value'),
      key: 'Value',
      dataIndex: 'Value',
      render: (value: any, record: any) => {
        if (record.Key === 'printMode') {
          return (
            <Select
              style={{ width: '100%' }}
              value={value}
              disabled
            >
              {printModeOptions.map((option) => (
                <Select.Option key={option.id} value={option.id}>
                  {option.name}
                </Select.Option>
              ))}
            </Select>
          );
        }
        if (Array.isArray(value)) {
          return (
            <Select
              mode="multiple"
              style={{ width: '100%' }}
              disabled
              value={value}
            >
              {value.map((item: any, index: number) => (
                <Select.Option key={index} value={item}>
                  {item}
                </Select.Option>
              ))}
            </Select>
          );
        }
        return renderValue(value);
      },
    },
  ];

  const printerBrand = {
    'Zebra': 0,
    'Sato': 1
  };

  const isAltisAdmin = userInfo?.role === UserRole.AltisAdmin;

  const columns: IMUIDataGridColumns[] = [
    ...(userInfo.role == UserRole.AltisAdmin ? [{
      title: t('Company Name'),
      key: 'companyName',
      isSorting: true,
      placeHolder: t('Company Name'),
    }] : []),
    {
      title: t('Printer Name'),
      key: 'printerName',
      isSorting: true,
      align: 'center',
      headerAlign: 'center',
      placeHolder: t('Printer Name')
    },
    {
      title: t('Printer Model'),
      key: 'printerModel',
      isSorting: true,
      placeHolder: t('Printer Model'),
      align: 'center',
      headerAlign: 'center',
      enumValues: printerBrand,
    },
    {
      title: t('Printer Code'),
      key: 'printerCode',
      isSorting: true,
      align: 'center',
      headerAlign: 'center',
      placeHolder: t('Printer Code'),
    },
    {
      title: t('Ip Address'),
      key: 'printerIpAddress',
      placeHolder: t('Ip Address'),
      align: 'center',
      headerAlign: 'center',
    },
    {
      title: t('Printer Port'),
      key: 'printerPort',
      placeHolder: t('Printer Port'),
      align: 'center',
      headerAlign: 'center',
    },
    {
      title: t('operations'),
      key: 'operations',
      align: 'center',
      headerAlign: 'center',
      hideColumnMenu: true,
      placeHolder: t('operations'),
      render: (order, record, index) => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', marginTop: 10, marginLeft: isAltisAdmin ? 10 : 30 }}>
          <Space size={10}>
            <Popconfirm
              title={t('delete_confirm')}
              onConfirm={() => {
                DeleteDevice(record.printerId);
              }}
              okText={t('yes')}
              cancelText={t('no')}
            >
              <Tooltip title={t('delete')}>
                <Button
                  size='small'
                  icon={<DeleteOutlined />}
                  style={{ display: userInfo.role === UserRole.Sayman ? 'none' : 'block', width: '60px', borderRadius: '70px' }}
                  onClick={(e) => e.stopPropagation()}
                >
                </Button>
              </Tooltip>

            </Popconfirm>
            <Tooltip title={t('update')}>
              <Button
                size='small'
                icon={<EditOutlined />}
                style={{ display: userInfo.role === UserRole.Sayman ? 'none' : 'block', width: '60px', borderRadius: '70px' }}
                onClick={() => {
                  window.location.replace(`/admin/update-printer/${record.printerId}`);
                }}
              >
              </Button>
            </Tooltip>

            <Tooltip title={t('details')}>
              <Button
                size='small'
                icon={<EyeOutlined />}
                style={{ display: userInfo.role === UserRole.Sayman ? 'none' : 'block', width: '60px', borderRadius: '70px' }}
                onClick={() => {
                  setSelectedPrinter(record);
                  setIsModalVisible(true);
                }}
              >
              </Button>
            </Tooltip>

          </Space>
        </div>
      ),
    },
  ];

  const PrinterFilter = (e?) => {
    const filters = { ...e?.filters };

    if ('printerModel' in filters && (filters.printerModel === "" || filters.printerModel === undefined || filters.printerModel === null)) {
      delete filters.printerModel;
    } else if ('printerModel' in filters) {
      filters.printerModel = Number(filters.printerModel);
    }
    const body = {
      sortColumn: e?.sortField,
      sortOrder: e?.sortOrder || 'asc',
      pageSize: e?.pageSize,
      page: e?.page,
       ...filters
    };

    HTTPService.POST(`/Printer/filters`, body).then((response) => {
      if (!response.data) {
        return;
      }

      if (response.status === 200) {
        setPrinterData(response.data);
      } else {
        toast.current?.show({
          severity: 'error',
          summary: t('mistake'),
          detail: t(response.data),
          life: 3000
        });
      }
    });
  };

  const DeleteDevice = (id) => {
    HTTPService.DELETE(`/Printer/deletePrinter?Id=${id}`).then((response) => {
      if (response.status === 200) {
        toast.current?.show({
          severity: 'success',
          summary: t('successful'),
          detail: t(response.data),
          life: 3000
        });
        dataGridRef.current.GetAllData()
      } else if (response.status === 400) {
        toast.current?.show({
          severity: 'error',
          summary: t('mistake'),
          detail: t(response.data),
          life: 3000
        });
      } else {
        toast.current?.show({
          severity: 'error',
          summary: t('mistake'),
          detail: t(response.data),
          life: 3000
        });
      }
    });
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setSelectedPrinter(null);
  };

  const printerDetailsToTableData = (printer: Printer) => {
    if (!printer.printerDetails) {
      return [];
    }
    return Object.entries(JSON.parse(printer.printerDetails)).map(([key, value]) => ({
      Key: key,
      Value: typeof value === 'boolean' ? value : JSON.stringify(value),
    }));
  };

  const modalWidth = window.innerWidth < 768 ? '90%' : 650;

  return (
    <>
      <Toast ref={toast} />
      <div className='row'>
        <div
          className='col-md-4 mb-3'
          style={{ display: userInfo.role === UserRole.Sayman ? 'none' : 'block' }}
        >
          <Tooltip title={t('Add New Printer')}>
            <Button
              type="primary"
              shape="round"
              style={{
                height: 64,
                width: 'auto',
                backgroundColor: 'transparent',
                color: '#1890ff',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                marginLeft: '10px',
                padding: 0,
                border: 'none',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.05)';
                e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
                e.currentTarget.style.color = '#40a9ff';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.color = '#1890ff';
              }}
              size="middle"
              onClick={() => window.location.replace('/admin/add-printer')}
            >
              <PrinterOutlined style={{ fontSize: 24 }} />
              <span style={{ fontSize: '12px', marginTop: '4px' }}>{t('Add New Printer')}</span>
            </Button>
          </Tooltip>
        </div>
      </div>
      <div className='row'>
        <div className='col-md-12'>
          <MUIDatagrid
            ref={dataGridRef}
            data={printerData?.data}
            total={printerData?.count}
            columns={columns}
            filterMode='server'
            onChange={PrinterFilter}
            enableSelection={false}
          />
        </div>
      </div>
      <Modal
        title={<span style={{ fontWeight: 'bold' }}>{t('details')}</span>}
        visible={isModalVisible}
        bodyStyle={{ padding: 15, maxHeight: '70vh', overflowY: 'auto' }}
        width={modalWidth} // Responsive width
        footer={null}
        onCancel={handleCancel}
      >
        <div style={{ overflowX: 'auto' }}>
          <Table
            key={"kfgjfdkgnmd"}
            pagination={false}
            columns={DetailsColumn}
            dataSource={selectedPrinter ? printerDetailsToTableData(selectedPrinter) : []}
            rowKey="Key"
            scroll={{ x: 'max-content' }} // Horizontal scroll if needed
          />
        </div>
      </Modal>
    </>
  );
};

export default PrinterManagement;
